'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useToast } from '../contexts/toast-context';
const toastIcons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
};
const toastStyles = {
    success: {
        light: 'bg-green-50 border-green-200 text-green-800',
        dark: 'bg-green-900/20 border-green-800/30 text-green-200',
        icon: 'text-green-500'
    },
    error: {
        light: 'bg-red-50 border-red-200 text-red-800',
        dark: 'bg-red-900/20 border-red-800/30 text-red-200',
        icon: 'text-red-500'
    },
    warning: {
        light: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        dark: 'bg-yellow-900/20 border-yellow-800/30 text-yellow-200',
        icon: 'text-yellow-500'
    },
    info: {
        light: 'bg-blue-50 border-blue-200 text-blue-800',
        dark: 'bg-blue-900/20 border-blue-800/30 text-blue-200',
        icon: 'text-blue-500'
    },
};
function ToastItem({ toast, onDismiss }) {
    const Icon = toastIcons[toast.type];
    const styles = toastStyles[toast.type];
    useEffect(() => {
        const timer = setTimeout(() => {
            onDismiss(toast.id);
        }, toast.duration || 5000);
        return () => clearTimeout(timer);
    }, [toast.id, toast.duration, onDismiss]);
    return (_jsxs("div", { className: `
        flex items-start p-4 rounded-lg border shadow-lg backdrop-blur-sm
        transform transition-all duration-300 ease-in-out
        animate-in slide-in-from-right-full
        ${styles.light} dark:${styles.dark}
        max-w-md w-full relative overflow-hidden
      `, style: {
            animation: 'slideInRight 0.3s ease-out'
        }, children: [toast.duration && toast.duration > 0 && (_jsx("div", { className: "absolute bottom-0 left-0 h-1 bg-current opacity-30 transition-all ease-linear", style: {
                    animation: `shrink ${toast.duration}ms linear`,
                    width: '100%'
                } })), _jsx("div", { className: "flex-shrink-0 mr-3", children: _jsx(Icon, { className: `w-5 h-5 ${styles.icon}` }) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h4", { className: "text-sm font-semibold mb-1", children: toast.title }), toast.message && (_jsx("p", { className: "text-sm opacity-90", children: toast.message })), toast.action && (_jsx("div", { className: "mt-2", children: _jsx("button", { onClick: toast.action.onClick, className: "text-sm font-medium underline hover:no-underline transition-all", children: toast.action.label }) }))] }), _jsx("button", { onClick: () => onDismiss(toast.id), className: "flex-shrink-0 ml-2 p-1 rounded-md hover:bg-black/10 dark:hover:bg-white/10 transition-colors", "aria-label": "Dismiss", children: _jsx(X, { className: "w-4 h-4 opacity-60 hover:opacity-100" }) })] }));
}
export function ToastContainer() {
    const { toasts, hideToast } = useToast();
    if (toasts.length === 0)
        return null;
    const containerContent = (_jsx("div", { className: "fixed top-4 right-4 z-[9999] flex flex-col space-y-2 pointer-events-none", style: {
            maxHeight: 'calc(100vh - 2rem)',
            overflowY: 'auto'
        }, children: toasts.map((toast) => (_jsx("div", { className: "pointer-events-auto", children: _jsx(ToastItem, { toast: toast, onDismiss: hideToast }) }, toast.id))) }));
    return typeof window !== 'undefined'
        ? createPortal(containerContent, document.body)
        : null;
}
// CSS animations to be added to global styles
export const toastAnimations = `
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes shrink {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

.animate-in {
  animation-fill-mode: both;
}

.slide-in-from-right-full {
  animation: slideInRight 0.3s ease-out;
}
`;
